import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ConsentBanner } from "./ConsentBanner";
import { Footer } from "./Footer";
import { Button } from "./ui/button";
import { MenuButton } from "./ui/menu-button";

// Background image used in the animation
const BACKGROUND_IMAGE = "/images/app-bg.jpg";
// ANYW3AR white logo
const ANYW3AR_LOGO = "/images/logo-typo-white.svg";
// Header height after collapse
const HEADER_HEIGHT = "3.75rem"; // 60px

export interface EntranceAnimationProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Callback invoked when the animation sequence completes
   */
  onComplete: () => void;
  /**
   * Whether the user has accepted the consent terms
   * Controls the Sign Up button disabled state
   */
  consentAccepted: boolean;
  /**
   * Callback invoked when consent checkbox state changes
   */
  onConsentChange: (accepted: boolean) => void;
  /**
   * Callback invoked when Sign Up button is clicked
   */
  onSignUpClick: () => void;
  /**
   * Preview mode - keeps animation running without auto-complete
   * Useful for Storybook and visual testing
   * @default false
   */
  preview?: boolean;
}

/**
 * EntranceAnimation - 8-step entrance animation for onboarding flow
 *
 * **Animation Sequence (~3.7s total):**
 * 1. Step 1 (0-750ms): Full-screen red background (#C80815 / bg-brand-red)
 * 2. Step 2 (750-1500ms): ANYW3AR logo fades in centered
 * 3. Step 3 (1500-2000ms): Red overlay COLLAPSES UP to menu bar height (60px)
 *    - Logo stays centered, ends at menu position
 *    - Background image revealed immediately (no fade)
 * 4. Step 4 (2000-2390ms): Hamburger line 1 draws (right → left)
 * 5. Step 5 (2390-2780ms): Hamburger line 2 draws (right → left)
 * 6. Step 6 (2780-3080ms): Hamburger line 3 draws (left → right)
 * 7. Step 7 (3080-3380ms): Sign Up button slides in from BOTTOM
 * 8. Step 8 (3380-3680ms): ConsentBanner fades in ON TOP
 *
 * **Consent Banner Behavior:**
 * - Fades out when user clicks "I ACCEPT" button
 *
 * **Architecture:**
 * - Background Image Layer (z-index: 0) - Always present
 * - Red Overlay (z-index: 10) - Collapses from 100vh to 60px
 * - Content Area (z-index: 5) - Appears after collapse
 *
 * **Figma References:**
 * - Step 1: https://www.figma.com/design/6winhIOT978DKg5JsK2mkW/Zone-de-travail-Arno-WebApp?node-id=275-11737
 * - Step 2: https://www.figma.com/design/6winhIOT978DKg5JsK2mkW/Zone-de-travail-Arno-WebApp?node-id=275-11653
 * - Step 3-6: Red collapses + menu lines draw
 * - Step 7-8: https://www.figma.com/design/6winhIOT978DKg5JsK2mkW/Zone-de-travail-Arno-WebApp?node-id=233-977
 *
 * **Accessibility:**
 * - Respects `prefers-reduced-motion` media query (skips to final state)
 * - Proper ARIA attributes for loading indication
 * - Button disabled state tied to consent acceptance
 *
 * @example
 * ```tsx
 * <EntranceAnimation
 *   onComplete={() => console.log('Animation complete')}
 *   consentAccepted={consentAccepted}
 *   onConsentChange={setConsentAccepted}
 *   onSignUpClick={() => navigate('/auth/signup')}
 * />
 * ```
 */
export const EntranceAnimation = React.forwardRef<
  HTMLDivElement,
  EntranceAnimationProps
>(
  (
    {
      onComplete,
      consentAccepted,
      onConsentChange,
      onSignUpClick,
      preview = false,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    // Current animation step (0-7)
    // 0: Red background
    // 1: Logo visible
    // 2: Collapsed (logo at top)
    // 3: Menu line 1
    // 4: Menu line 2
    // 5: Menu line 3
    // 6: Sign-up visible
    // 7: Consent visible
    const [currentStep, setCurrentStep] = React.useState(0);

    // Check for prefers-reduced-motion
    const prefersReducedMotion = React.useMemo(
      () =>
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      []
    );

    // Animation step progression
    React.useEffect(() => {
      // If user prefers reduced motion, skip to final state immediately
      if (prefersReducedMotion) {
        setCurrentStep(7);
        if (!preview) {
          onComplete();
        }
        return;
      }

      // Animation timing (ms)
      // Note: First phase (until collapse) extended by 50%
      // Note: Hamburger animation (steps 3-5) has 390ms gaps (30% longer than original 300ms)
      const delays = [
        0, // Step 0: Red background
        750, // Step 1: Logo fades in (was 500ms, +50%)
        1500, // Step 2: Collapse starts (was 1000ms, +50%)
        2000, // Step 3: Menu line 1 (top, right→left)
        2390, // Step 4: Menu line 2 (middle, right→left)
        2780, // Step 5: Menu line 3 (bottom, left→right)
        3080, // Step 6: Sign-up slides up
        3380, // Step 7: Consent fades in
      ];

      const timers = delays.map((delay, index) =>
        setTimeout(() => {
          setCurrentStep(index);
          // Call onComplete after final step (unless in preview mode)
          if (index === 7 && !preview) {
            // Add a small delay for the last animation to complete
            setTimeout(onComplete, 300);
          }
        }, delay)
      );

      return () => timers.forEach(clearTimeout);
    }, [prefersReducedMotion, onComplete, preview]);

    // Step visibility helpers
    const showLogo = currentStep >= 1;
    const isCollapsed = currentStep >= 2;
    const menuLineStep = currentStep >= 3 ? Math.min(currentStep - 2, 3) : 0; // 0, 1, 2, or 3
    const showSignUp = currentStep >= 6;
    const showConsent = currentStep >= 7;

    // Transition classes based on reduced motion preference
    const transitionClass = prefersReducedMotion
      ? ""
      : "transition-all duration-500 ease-out";

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label="Loading onboarding"
        className={cn("fixed inset-0 flex flex-col overflow-hidden", className)}
        {...props}
      >
        {/* Background Image Layer - Always present, z-index: 0 */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${BACKGROUND_IMAGE})`,
          }}
          aria-hidden="true"
        />

        {/* Red Overlay - Collapses from full screen to header height */}
        <div
          className={cn(
            "fixed top-0 left-0 right-0 bg-brand-red z-10 flex items-center justify-center",
            transitionClass
          )}
          style={{
            height: isCollapsed ? HEADER_HEIGHT : "100vh",
          }}
        >
          {/* Logo - centered, stays in place during collapse */}
          <img
            src={ANYW3AR_LOGO}
            alt="ANYW3AR"
            className={cn(
              "h-[2.375rem] w-auto",
              transitionClass,
              showLogo ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
          />

          {/* Menu Button - appears at right side after collapse, lines draw sequentially */}
          <div
            className={cn(
              "absolute right-4 top-1/2 -translate-y-1/2",
              transitionClass,
              isCollapsed ? "opacity-100" : "opacity-0"
            )}
          >
            <MenuButton
              isOpen={false}
              animateIn={true}
              animateStep={menuLineStep as 0 | 1 | 2 | 3}
              className="relative z-50"
              aria-label="Open menu"
            />
          </div>
        </div>

        {/* Content Area - appears after collapse */}
        <div
          className={cn(
            "flex-1 flex flex-col",
            // Push content below the collapsed header
            isCollapsed ? "pt-[3.75rem]" : "pt-0"
          )}
        >
          {/* Spacer to push content to bottom */}
          <div className="flex-1" />

          {/* Footer area - ConsentBanner + Sign Up button */}
          <div
            className={cn("relative z-5 flex flex-col items-center pb-8 px-8")}
          >
            {/* Constrain content to mobile width */}
            <div className="w-full max-w-[21.4375rem] flex flex-col gap-4">
              {/* Consent Banner - ON TOP with checkbox + "I ACCEPT" button */}
              {/* Hides after user clicks "I ACCEPT" */}
              <div
                className={cn(
                  transitionClass,
                  showConsent && !consentAccepted ? "opacity-100" : "opacity-0",
                  consentAccepted ? "pointer-events-none" : ""
                )}
              >
                <ConsentBanner
                  showButton={true}
                  onAccept={onConsentChange}
                  termsUrl="https://anyw3ar.com/terms"
                  privacyUrl="https://anyw3ar.com/privacy"
                  cookiesUrl="https://anyw3ar.com/cookies"
                />
              </div>

              {/* Sign Up Button - BELOW consent banner, slides in from bottom */}
              <div
                className={cn(
                  transitionClass,
                  showSignUp
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
              >
                <Button
                  variant="primary-dark"
                  size="primary-dark"
                  disabled={!consentAccepted}
                  onClick={onSignUpClick}
                  className="w-full uppercase disabled:opacity-80"
                >
                  {t("onboarding.signUp", "SIGN UP")}
                </Button>
              </div>

              {/* Footer */}
              <Footer className="mt-4" />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

EntranceAnimation.displayName = "EntranceAnimation";
